import DownloadIcon from '@/assets/icons/DownloadIcon'
import SearchIconBtn from '@/assets/icons/SearchIconBtn'
import CustomButton from '@/components/Returns/CustomButton'
import { ConfirmationDialog } from '@/components/common'
import Search from '@/components/common/Search'
import { GLOBAL_CONSTANTS } from '@/constants/globalConstants'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import { useDefineDataType } from '@/hooks/useDefineDataType'
import { MTCommonTable } from '@/multi-territory/mt-components/mt-shared/MTCommonTable'
import {
    createServiceMasterRecord,
    deleteServiceMasterRecord,
    downloadServiceMaster,
    fetchServiceMasterList,
    fetchServiceMasterMetadata,
    metadataToFormFields,
    metadataToTableColumns,
    triggerBlobDownload,
    updateServiceMasterRecord,
} from '@/multi-territory/mt-services/mt-service-master-api'
import type {
    MTMetadataFormField,
    MTMetadataTableColumn,
    MTServiceMasterRecord,
} from '@/multi-territory/mt-types/mt-metadata'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { MTServiceKeywordForm } from './MTServiceKeywordForm'
import { formatMTHeaderLabel } from '@/multi-territory/mt-utils/mt-search-fields'
const ServiceKeywordSearch = Search as any

/**
 * MTServiceKeywordTable - Metadata-driven Service Keyword table with full API integration.
 *
 * APIs integrated:
 * - POST /service-keyword/list (paginated list with search)
 * - POST /service-keyword/add (create)
 * - PUT /service-keyword/:uuid (update)
 * - POST /service-keyword/download (Excel export)
 * - GET /data/{countryCode}/service_keyword/metadata (column/form config)
 */
export function MTServiceKeywordTable() {
  // Derive dataType from navigation API instead of hardcoding
  const { dataType, dataTypeLower } = useDefineDataType('SECTION_KEYWORD')
  const DATA_TYPE = dataType ?? 'SECTION_KEYWORD'
  const DATA_TYPE_LOWER = dataTypeLower ?? 'section-keyword'

  const [columns, setColumns] = useState<MTMetadataTableColumn[]>([])
  const [formFields, setFormFields] = useState<MTMetadataFormField[]>([])
  const [formTitle, setFormTitle] = useState<string>('Add service keyword')
  const [data, setData] = useState<MTServiceMasterRecord[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [loading, setLoading] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    GLOBAL_CONSTANTS.DEFAULT_PAGE_SIZE || 10
  )
  const [editMode, setEditMode] = useState(false)
  const [editingRowId, setEditingRowId] = useState<string | null>(null)
  const [editFormData, setEditFormData] = useState<Record<
    string,
    string
  > | null>(null)
  const [editingData, setEditingData] = useState<Record<string, any>>({})
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const searchIconRef = useRef<HTMLButtonElement>(null)
  const [searchCOList, setSearchCOList] = useState<any[]>([])
  const [searchCriteria, setSearchCriteria] = useState<any[]>([
    {
      id: 1,
      logicalOperator: 'Where',
      field: '',
      value: '',
    },
  ])
  const [downloading, setDownloading] = useState(false)
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [showTable, setShowTable] = useState(false)
  const [rawMetadataFields, setRawMetadataFields] = useState<any[]>([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null)

  const { showSnackbar } = useSnackbar()
  const countryCode = useSelector(
    (state: { country: { countryCode: string } }) => state.country.countryCode
  )
  const selectedEntityUuid = useSelector(
    (state: any) => state.mtEntityTan?.selectedEntityUuid ?? ''
  )
  const selectedTanUuid = useSelector(
    (state: any) => state.mtEntityTan?.selectedTanUuid ?? ''
  )
  const entityMetaByUuid = useSelector(
    (state: any) => state.mtEntityTan?.entityMetaByUuid ?? {}
  )
  const selectedEntityMeta = entityMetaByUuid[selectedEntityUuid] || null
  const tanApplicable = selectedEntityMeta?.tanApplicable ?? true

  /**
   * API identifier logic:
   * - tanApplicable=true  -> send selectedTanUuid (tanMasterUuid)
   * - tanApplicable=false -> send selectedEntityUuid (entityMasterUuid)
   */
  const apiIdentifierUuid = tanApplicable ? selectedTanUuid : selectedEntityUuid

  // Load metadata on mount / countryCode change (no dependency on viewTrigger)
  const loadMetadata = useCallback(async () => {
    if (!countryCode || !DATA_TYPE) return
    const metaResult = await fetchServiceMasterMetadata(countryCode, DATA_TYPE)
    if (metaResult.success) {
      if (metaResult.data.form_title) setFormTitle(metaResult.data.form_title)
      setColumns(metadataToTableColumns(metaResult.data.fields))
      setFormFields(metadataToFormFields(metaResult.data.fields))
      setRawMetadataFields(metaResult.data.fields || [])
    }
  }, [countryCode, DATA_TYPE])

  useEffect(() => {
    loadMetadata()
  }, [loadMetadata])

  const loadData = useCallback(
    async (searchList?: any[]) => {
      if (!countryCode || !apiIdentifierUuid || !DATA_TYPE_LOWER) return
      setLoading(true)
      const payload = {
        pageFilter: {
          currentPage,
          pageSize: itemsPerPage,
          sortField: 'lastModifiedDate',
          sortOrder: 'DESC' as const,
        },
        searchCOList: searchList ?? searchCOList,
      }
      const dataResult = await fetchServiceMasterList(
        countryCode,
        apiIdentifierUuid,
        payload,
        DATA_TYPE_LOWER,
        { tanApplicable }
      )
      if (dataResult.success) {
        setData(
          dataResult.data.content.map((row, idx) => ({
            ...row,
            uuid:
              row.associated_uuid ||
              row.global_uuid ||
              row.uuid ||
              row.id ||
              `row-${idx}`,
          }))
        )
        setTotalItems(dataResult.data.totalElements)
      } else {
        showSnackbar(dataResult.error, 'error')
      }
      setLoading(false)
    },
    [
      countryCode,
      apiIdentifierUuid,
      currentPage,
      itemsPerPage,
      searchCOList,
      showSnackbar,
    ]
  )

  // Get viewTrigger from Redux (incremented when View button is clicked)
  const viewTrigger = useSelector(
    (state: any) => state.mtEntityTan?.viewTrigger ?? 0
  )

  const lastViewTriggerRef = useRef(viewTrigger)

  // Load data when View button is clicked (viewTrigger increments)
  useEffect(() => {
    if (viewTrigger > 0 && apiIdentifierUuid && countryCode) {
      if (viewTrigger <= lastViewTriggerRef.current) return
      lastViewTriggerRef.current = viewTrigger
      setShowTable(true)
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewTrigger])

  // Reload data when pagination changes (only if table is already shown)
  useEffect(() => {
    if (showTable && apiIdentifierUuid && countryCode) {
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage])

  const visibleColumns = useMemo(
    () => columns.filter(col => !col.id.startsWith('sys_')),
    [columns]
  )

  // Add Action column to visible columns for the table header
  const tableColumns = useMemo(() => {
    const actionCol: MTMetadataTableColumn = {
      id: '_action',
      label: 'Action',
      type: 'text',
      sortable: false,
      editable: false,
      width: '30px',
      minWidth: '50px',
    }
    return [...visibleColumns, actionCol]
  }, [visibleColumns])

  const searchFields = useMemo(
    () =>
      visibleColumns.map(col => {
        // Match the advance-search label to the table header column exactly
        // (sentence case / "running text", acronyms such as ID/TIN uppercase).
        const label = formatMTHeaderLabel(col.label)
        return {
          id: col.id,
          label,
          type:
            col.type === 'date'
              ? 'date'
              : col.type === 'dropdown'
                ? 'dropdown'
                : 'text',
          placeholder:
            col.type === 'date'
              ? 'DD/MM/YYYY'
              : col.type === 'dropdown'
                ? `Select ${label}`
                : `Enter ${label}`,
          ...(col.type === 'dropdown' ? { options: col.options ?? [] } : {}),
        }
      }),
    [visibleColumns]
  )

  // --- Per-row Edit handlers ---
  const handleFieldChange = (
    _rowId: string,
    fieldKey: string,
    value: string
  ) => {
    setEditingData(p => ({ ...p, [fieldKey]: value }))
  }

  const validateEditingData = (): boolean => {
    for (const field of rawMetadataFields) {
      if (!field.editable || !field.visible) continue
      const value = String(editingData[field.field_key] ?? '').trim()

      if (field.required && (!value || value === '-')) {
        showSnackbar(
          field.validations?.required_message || `${field.label} is required`,
          'error'
        )
        return false
      }
      if (!value || value === '-') continue

      if (field.type === 'number') {
        const numVal = parseFloat(value)
        if (isNaN(numVal)) {
          showSnackbar(`${field.label} must be a valid number`, 'error')
          return false
        }
        if (
          field.validations?.min !== undefined &&
          numVal < field.validations.min
        ) {
          showSnackbar(
            `${field.label} must be at least ${field.validations.min}`,
            'error'
          )
          return false
        }
        if (
          field.validations?.max !== undefined &&
          numVal > field.validations.max
        ) {
          showSnackbar(
            `${field.label} must not exceed ${field.validations.max}`,
            'error'
          )
          return false
        }
      }

      if (
        field.validations?.maxLength &&
        value.length > field.validations.maxLength
      ) {
        showSnackbar(
          `${field.label} exceeds max length ${field.validations.maxLength}`,
          'error'
        )
        return false
      }

      if (
        field.validations?.regex &&
        field.type !== 'dropdown' &&
        field.type !== 'date'
      ) {
        try {
          if (!new RegExp(field.validations.regex).test(value)) {
            showSnackbar(
              field.validations.regex_message ||
                `${field.label} has invalid format`,
              'error'
            )
            return false
          }
        } catch {
          /* skip */
        }
      }
    }
    return true
  }

  const handleSaveEdit = async () => {
    if (!editingRowId || !validateEditingData()) return
    if (editingRowId.startsWith('row-')) {
      showSnackbar(
        'Cannot save: record UUID not found. Try refreshing the data.',
        'error'
      )
      return
    }
    const typedData: Record<string, any> = { ...editingData }
    for (const field of rawMetadataFields) {
      if (field.type === 'number' && typedData[field.field_key] !== undefined) {
        const raw = String(typedData[field.field_key] ?? '').trim()
        typedData[field.field_key] = raw ? parseFloat(raw) : null
      }
    }
    const result = await updateServiceMasterRecord(
      countryCode,
      apiIdentifierUuid,
      editingRowId,
      typedData,
      DATA_TYPE_LOWER,
      { tanApplicable }
    )
    if (result.success) {
      showSnackbar(result.data || 'Record updated successfully', 'success')
      handleCancelEdit()
      loadData()
    } else {
      showSnackbar(result.error, 'error')
    }
  }

  const handleCancelEdit = () => {
    setEditMode(false)
    setEditingRowId(null)
    setEditingData({})
  }

  const handleSelectItem = (uuid: string) => {
    setSelectedItems(p =>
      p.includes(uuid) ? p.filter(id => id !== uuid) : [...p, uuid]
    )
  }
  const handleSelectAll = () => {
    setSelectedItems(p =>
      p.length === data.length ? [] : data.map(r => r.uuid as string)
    )
  }

  const handlePageChange = (page: number) => setCurrentPage(page)
  const handleItemsPerPageChange = (size: number) => {
    setItemsPerPage(size)
    setCurrentPage(1)
  }

  const handleAdd = () => {
    setEditFormData(null)
    setEditingRowId(null)
    setFormOpen(true)
  }
  const handleFormClose = () => {
    setFormOpen(false)
    setEditFormData(null)
    setEditingRowId(null)
  }
  const handleFormSubmit = async (
    formData: Record<string, string | number | null>
  ): Promise<boolean> => {
    let result
    if (editingRowId && editFormData) {
      // Update existing record
      result = await updateServiceMasterRecord(
        countryCode,
        apiIdentifierUuid,
        editingRowId,
        formData,
        DATA_TYPE_LOWER,
        { tanApplicable }
      )
    } else {
      // Create new record
      result = await createServiceMasterRecord(
        countryCode,
        apiIdentifierUuid,
        formData,
        DATA_TYPE_LOWER,
        { tanApplicable }
      )
    }
    if (result.success) {
      showSnackbar(
        result.data ||
          (editFormData
            ? 'Record updated successfully'
            : 'Record created successfully'),
        'success'
      )
      loadData()
      return true
    } else {
      showSnackbar(result.error, 'error')
      return false
    }
  }

  const handleDownload = async () => {
    if (!countryCode || !apiIdentifierUuid) {
      showSnackbar(
        'Country and Entity must be selected for download',
        'warning'
      )
      return
    }
    setDownloading(true)
    const result = await downloadServiceMaster(
      countryCode,
      apiIdentifierUuid,
      searchCOList.length > 0 ? { searchCOList } : undefined,
      DATA_TYPE_LOWER,
      { tanApplicable }
    )
    if (result.success) {
      triggerBlobDownload(
        result.data,
        `service_keyword_${countryCode.toLowerCase()}.xlsx`
      )
      showSnackbar('Download started', 'success')
    } else {
      showSnackbar(result.error || 'Download failed', 'error')
    }
    setDownloading(false)
  }

  const handleRowEdit = (uuid: string) => {
    const row = data.find(
      r => (r.associated_uuid || r.global_uuid || r.uuid || r.id) === uuid
    )
    if (!row) return
    // Pre-fill form with row's current values
    const prefilled: Record<string, string> = {}
    formFields.forEach(field => {
      prefilled[field.key] =
        row[field.key] != null ? String(row[field.key]) : ''
    })
    setEditFormData(prefilled)
    setEditingRowId(uuid)
    setFormOpen(true)
  }

  // Open the confirmation dialog before deleting (parity with Duplication Rule)
  const handleRowDelete = (uuid: string) => {
    if (uuid.startsWith('row-')) {
      showSnackbar('Cannot delete: record UUID not found.', 'error')
      return
    }
    setDeletingUuid(uuid)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!deletingUuid) return
    setDeleteDialogOpen(false)

    const result = await deleteServiceMasterRecord(
      countryCode,
      deletingUuid,
      DATA_TYPE_LOWER,
      apiIdentifierUuid,
      { tanApplicable }
    )
    if (result.success) {
      showSnackbar(result.data || 'Record deleted successfully', 'success')
      loadData()
    } else {
      showSnackbar(result.error || 'Delete failed', 'error')
    }
    setDeletingUuid(null)
  }

  const openSearchDialog = (event: React.MouseEvent<HTMLElement>) => {
    if (
      searchCriteria.length > 0 &&
      !searchCriteria[0].field &&
      visibleColumns.length > 0
    ) {
      setSearchCriteria(prev =>
        prev.map((c, i) =>
          i === 0 ? { ...c, field: visibleColumns[0].id } : c
        )
      )
    }
    setAnchorEl(event.currentTarget)
    setSearchOpen(true)
  }
  const closeSearchDialog = () => {
    setSearchOpen(false)
    setAnchorEl(null)
  }
  const applySearch = async (searchList: any[]) => {
    // The shared Search component already emits fully-formatted criteria:
    // { fieldName, operator, value } for scalar operators and
    // { fieldName, operator, searchValueList } for the IN_LIST / NOT_IN_LIST /
    // *_PROBABLE_MATCH family (no `value` key). Store/send it as-is — the old
    // `.filter(i => i.value)` dropped every list-operator criterion and the
    // rebuild dropped `searchValueList`.
    setSearchCOList(searchList)
    setCurrentPage(1)
    setSearchCriteria(
      searchList.map((i, idx) => ({
        ...i,
        id: idx + 1,
        logicalOperator: i.logicalOperator || (idx === 0 ? 'Where' : 'And'),
      }))
    )
    setSearchOpen(false)
    loadData(searchList)
  }
  const resetSearch = () => {
    setSearchCOList([])
    setSearchCriteria([
      { id: 1, logicalOperator: 'Where', field: '', value: '' },
    ])
    setCurrentPage(1)
    setSearchOpen(false)
    loadData([])
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {showTable && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              my: '16px',
            }}
          >
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 500,
                lineHeight: '130%',
                color: 'var(--text-heading)',
              }}
            >
              Service keywords
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Tooltip
                title={downloading ? 'Downloading...' : 'Download Excel'}
              >
                <IconButton
                  size="medium"
                  onClick={handleDownload}
                  disabled={downloading}
                  aria-label="Download"
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Search">
                <IconButton
                  size="medium"
                  ref={searchIconRef}
                  onClick={openSearchDialog}
                  aria-label="Search"
                >
                  <SearchIconBtn />
                </IconButton>
              </Tooltip>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    height: '16px',
                    borderLeft: '1px solid var(--border-subtel)',
                  }}
                />
                <CustomButton onClick={handleAdd}>Add</CustomButton>
              </Box>
            </Box>
          </Box>
          <MTCommonTable
            columns={tableColumns}
            data={data}
            loading={loading}
            showFooter
            editMode={editMode}
            editingRowId={editingRowId}
            editingData={editingData}
            onFieldChange={handleFieldChange}
            rawMetadataFields={rawMetadataFields}
            onRowEdit={handleRowEdit}
            onRowDelete={handleRowDelete}
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            emptyMessage="No service keyword data available"
          />
          <MTServiceKeywordForm
            open={formOpen}
            onClose={handleFormClose}
            title={editFormData ? formTitle.replace('Add', 'Edit') : formTitle}
            fields={formFields}
            onSubmit={handleFormSubmit}
            initialValues={editFormData}
          />
          <ServiceKeywordSearch
            open={searchOpen}
            anchorEl={anchorEl}
            fields={searchFields as any}
            searchCriteria={searchCriteria}
            setSearchCriteria={setSearchCriteria}
            onClose={closeSearchDialog}
            onApply={applySearch}
            onReset={resetSearch}
            placement="bottom-start"
          />
        </>
      )}

      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Service Keyword"
        message="Are you sure you want to delete this service keyword? This action cannot be undone."
        confirmText="Delete"
        type="warning"
        showCancelButton={true}
        onCancel={() => {
          setDeleteDialogOpen(false)
          setDeletingUuid(null)
        }}
      />
    </Box>
  )
}
